<svelte:options customElement={{ tag: 'vv-bills', shadow: 'none' }} />

<script context="module">
    import appCssUrl from '../../app.css?inline';
    const style = '<style' + `>${appCssUrl}</style>`;
</script>

<script lang="ts">
    import Router from 'svelte-spa-router';
    import { wrap } from 'svelte-spa-router/wrap';
    import Index from './Index.svelte';
    import BillDetailPage from './BillDetailPage.svelte';
    import OfficialProfilePage from './OfficialProfilePage.svelte';

    const routes = {
        '/': Index,
        '/Bill/:uri': wrap({ component: BillDetailPage }),
        '/Official/:id': wrap({ component: OfficialProfilePage }),
        '*': Index
    };
</script>

{@html style}

<div class="interfont">
    <Router {routes} />
</div>

<style>
    /* Custom elements are inline by default; the example pages don't notice because
       there is no site chrome. On UserSite the host must be a block. */
    :global(vv-bills) {
        display: block;
    }

    /*
     * UserSite .vvBody rules are more specific than Tailwind utilities, so the
     * example pages look right and /Bills does not. Events/Petitions already
     * pin the same properties with :global + !important.
     */
    :global(.vvBody vv-bills),
    :global(.vvBody vv-bills *:not(.ck-content):not(.ck-content *)) {
        word-spacing: normal !important;
        letter-spacing: normal !important;
    }

    :global(.vvBody vv-bills .tracking-wide) {
        letter-spacing: 0.025em !important;
    }

    :global(.vvBody vv-bills ul),
    :global(.vvBody vv-bills ol) {
        padding-left: 0 !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        list-style: none !important;
    }

    :global(.vvBody vv-bills ul.list-disc) {
        padding-left: 1.25rem !important;
        list-style-type: disc !important;
    }

    :global(.vvBody vv-bills li) {
        margin: 0 !important;
        list-style: none;
    }

    :global(.vvBody vv-bills ul.list-disc > li) {
        list-style-type: disc !important;
        margin-bottom: 0.25rem !important;
    }

    :global(.vvBody vv-bills a) {
        text-decoration: none !important;
    }

    :global(.vvBody vv-bills .underline),
    :global(.vvBody vv-bills .hover\:underline:hover),
    :global(.vvBody vv-bills .group:hover .group-hover\:underline) {
        text-decoration: underline !important;
    }

    /*
     * `font: inherit !important` (the shorthand) used to sit here. It resets font-size,
     * font-weight and line-height to inherit too, which silently discarded every button's own
     * text-sm/font-medium/font-semibold classes (e.g. the position filter chips rendered at 16px/
     * weight 100 instead of 14px/500) -- invisible on examples/ since this rule needs .vvBody.
     * Tailwind's own preflight already sets font-family: inherit for buttons (non-important), and
     * the only unconditional .vvBody button rule is padding/border/cursor, so font-family is the
     * only property that ever needed forcing here.
     */
    :global(.vvBody vv-bills button) {
        font-family: inherit !important;
    }

    /*
     * .vvBody input/select/textarea rules set a fixed font-family directly on the element
     * (not inherited), so it beats interfont's inherited vv-inter regardless of specificity.
     * Height, margin and color are handled per-element with Tailwind `!` utilities instead --
     * a blanket height:auto here would fight the pill select's own !h-full.
     */
    :global(.vvBody vv-bills select),
    :global(.vvBody vv-bills input) {
        font-family: vv-inter, 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    }

    :global(.vvBody vv-bills h1),
    :global(.vvBody vv-bills h2),
    :global(.vvBody vv-bills h3) {
        text-transform: none;
    }

    :global(.vvBody vv-bills .uppercase) {
        text-transform: uppercase !important;
    }

    /*
     * app.css defines --vv-grey-light/--vv-black etc. on :host, which only matches a shadow
     * root. Directory mounts vv-directory with shadow: true, so Profile.svelte's var(--vv-*)
     * reads resolve there; embedded in shadow-less vv-bills, :host never matches, so those
     * vars are undefined and border-color/fill silently fall back to currentcolor/black
     * (the profile's divider line and icons). This has nothing to do with .vvBody -- it's
     * missing on examples/ too -- so don't scope this one under .vvBody like the rest below.
     * Re-declare the ones Profile.svelte reads.
     */
    :global(vv-bills .bills-official-profile) {
        --vv-grey-light: #F2F2F2;
        --vv-black: #333333;
    }

    /*
     * The directory's Profile view relies on bare h2/h3/h4, ul/li and pre looking like body text
     * (Tailwind preflight + app.css). In the directory that holds because it renders in a shadow
     * root; embedded in vv-bills the site template's heading, list and pre styles reach it.
     */
    :global(.vvBody vv-bills .bills-official-profile :is(h1, h2, h3, h4, h5, h6)) {
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        background: none !important;
        color: inherit !important;
        font-family: inherit !important;
        line-height: inherit !important;
        text-transform: none !important;
    }

    :global(.vvBody vv-bills .bills-official-profile :is(h1, h2, h3, h4, h5, h6):not([class*='text-'])) {
        font-size: inherit !important;
    }

    :global(.vvBody vv-bills .bills-official-profile :is(h1, h2, h3, h4, h5, h6):not([class*='font-'])) {
        font-weight: inherit !important;
    }

    :global(.vvBody vv-bills .bills-official-profile pre) {
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        border-radius: 0 !important;
        background: none !important;
        color: inherit !important;
        font-family: inherit !important;
        font-size: inherit !important;
        line-height: inherit !important;
        overflow: visible !important;
    }

    :global(.vvBody vv-bills .bills-official-profile ul:not([class*='list-none'])) {
        padding-left: 2em !important;
        margin-bottom: 1em !important;
        list-style-type: disc !important;
    }

    :global(.vvBody vv-bills .bills-official-profile ul.pl-8) {
        padding-left: 2rem !important;
    }

    :global(.vvBody vv-bills .bills-official-profile ul:not([class*='list-none']) > li) {
        list-style-type: disc !important;
        margin-bottom: 0.25em !important;
    }

    :global(.vvBody vv-bills .bills-official-profile .accordion-header) {
        border: 0 !important;
        border-radius: 0 !important;
        background: none !important;
        box-shadow: none !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        color: inherit !important;
        text-align: left !important;
    }

    :global(.vvBody vv-bills .bills-official-profile .accordion-header:hover) {
        background-color: var(--vv-grey-light) !important;
    }
</style>
